import * as langs from "../utils/languages";
import timezones from "../utils/timezones";

/* eslint-disable no-unused-vars */
/**
 * @typedef {Object} PersonalInfo
 * @property {String} name
 * @property {String} surname
 * @property {String} username
 * @property {String} password
 */

/**
 * @typedef {Object} ContactInfo
 * @property {String} email
 * @property {String} phone
 * @property {String} privatePhone
 */

/**
 * @typedef {Object} AddressInfo
 * @property {String} city
 * @property {String} state
 * @property {String} country
 * @property {String} timezone
 */

/**
 * @typedef {Object} AdditionalInfo
 * @property {String} firm
 * @property {String} bio
 * @property {String} motto
 */

/**
 * @typedef {Object} ValidationResult
 * @property {String[]} errors
 * @property {boolean} valid
 */

export default class UserData {
  _userInfo = undefined;
  _isValid = undefined;
  _id = undefined;
  _counter = 20;
  _mottoCounter = 100;
  _creationValidation = [
    (v, c) => (!!v.username && /^[A-Za-z0-9\-!@#$%^&_*.]+$/.test(v.username) && v.username.length <= c) ||
      "Username is required and must contains letters, numbers or symbols in this range: -!@#$%^&_*. (spaces are not allowed)",
    (v, _c) =>
      (!!v.password && /^((?!.*[\s])(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-!@#$%^&_*]).{8,16})/.test(v.password)) ||
      "Password must have between 8 and 16 characters and it must contain at least: a capital letter, a number and a symbol (-!@#$%^&_*)"
  ];
  _credentialsValidations = [
    (v, _c) => !!v.name || "Invalid name",
    (v, _c) => !!v.surname || "Invalid surname",
    (v, _c) => (!!v.email && /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,})$/.test(v.email)) || "Invalid email",
  ];
  _validations = [
    (v, _c) => (!!v.firm && v.firm.length > 0) || "Invalid name of firm",
    (v, _c) => (!!v.city && v.city.length > 0) || "Invalid city",
    (v, _c) => (!!v.state && v.state.length > 0) || "Invalid state",
    (v, _c) => (!!v.country && v.country.length > 0) || "Invalid continent",
    (v, _c) => !!v.phone || "Phone number is required",
    (v, _c) => /^\+[1-9]\d{1,14}$/.test(v.phone) || "Phone number must be valid (example: +123456789)",
    (v, _c) => 
      (v.privatePhone == undefined || v.privatePhone.length == 0 || /^\+[1-9]\d{1,14}$/.test(v.privatePhone)) ||
      "Phone number must be valid (example: +123456789) or omitted",
    (v, _c) => !!v.tz || "Invalid timezone",
    (v, _c) => (!!v.spoken && v.spoken.length > 0) || "Insert at least one spoken language",
    (v, _c) => (!!v.bio && v.bio.length >= 1400 && v.bio.length <= 1800) ||
      `Bio min. 1400 max. 1800 characters, written: ${v.bio != undefined ? v.bio.length : "0"}`,
    (v, _c) => (!!v.picture && v.picture.length > 0) || "Upload a picture",
    (v, _c) => (!!v.location && v.location.length > 0) || "Provide the picture location",
    (v, _c) => (!!v.expertise && v.expertise.length >= 1 && !!v.expertise[0]) || "Insert at least one valid expertise area",
    (v, _c) =>
      ((!!v.yearsOfExperience || !!v.yearsOfExperience == 0) && v.yearsOfExperience >= 0) ||
      "Years of experience must be more or equal to 0",
    (v, _c) => (!!v.motto && v.motto.length <= (new UserData())._mottoCounter) ||
      `Motto is required and must be maximum ${(new UserData())._mottoCounter} character long`
  ];

  /**
   * Create a new user data object
   * @public @static
   * 
   * @param {PersonalInfo} personalInfo 
   * @param {ContactInfo} contactInfo 
   * @param {AddressInfo} addressInfo 
   * @param {AdditionalInfo} otherInfo 
   * @param {String[]} expertise 
   * @param {String[]} spokenLanguages
   */
  static create (defaultData = undefined) {
    const userData = new UserData();

    userData._init(defaultData);

    return userData;
  }

  // Getters and setters

  /**
   * Returns a data model for the user information
   *
   * @readonly
   * @memberof UserData
   */
  get userDataObject () {
    const tmp = Object.assign({}, this._userInfo);

    if (tmp.spoken != undefined) { tmp.spokenLanguages = tmp.spoken; }
    if (tmp.tz != undefined) { tmp.timezone = tmp.tz; }

    // Clear the object for JSON payload
    tmp.spoken = undefined;
    tmp.tz = undefined;

    // Remove calculated field
    delete tmp.nameKey;
    delete tmp.roles;

    return tmp;
  }

  get isValid () { return this._isValid; }
  get id () { return this._id; }
  get counter () { return this._counter; }
  get mottoCounter () { return this._mottoCounter; }
  get name () { return this._userInfo.name; }
  get surname () { return this._userInfo.surname; }
  get username () { return this._userInfo.username; }
  get password () { return this._userInfo.password; }
  get email () { return this._userInfo.email; }
  get phone () { return this._userInfo.phone; }
  get privatePhone () { return this._userInfo.privatePhone; }
  get city () { return this._userInfo.city; }
  get state () { return this._userInfo.state; }
  get country () { return this._userInfo.country; }
  get flag () { return this._userInfo.flag; }
  get tz () { return this._userInfo.tz; }
  get timezone () { return this._userInfo.timezone; }
  get firm () { return this._userInfo.firm; }
  get bio () { return this._userInfo.bio; }
  get link() { return this._userInfo.link; }
  get motto () { return this._userInfo.motto; }
  get expertise () { return this._userInfo.expertise; }
  get yearsOfExperience () { return this._userInfo.yearsOfExperience; }
  get spoken () { return this._userInfo.spoken; }
  get spokenLanguages () { return this._userInfo.spokenLanguages; }
  get picture () { return this._userInfo.picture; }
  get location () { return this._userInfo.location; }
  get terms () { return this._userInfo.terms; }
  get nameKey () { return this._userInfo.nameKey; }
  get spentPoints() { return this._userInfo.spentPoints; }
  get creationDate() {
    return this._userInfo.creationDate;
  }
  get roles() {
    return this._userInfo.roles;
  }

  set name (name) { this._userInfo.name = name; }
  set surname (surname) { this._userInfo.surname = surname; }
  set username (username) { this._userInfo.username = username; }
  set password (password) { this._userInfo.password = password; }
  set email (email) { this._userInfo.email = email; }
  set phone (phone) { this._userInfo.phone = phone.replace(" ", ""); }
  set privatePhone (privatePhone) { this._userInfo.privatePhone = privatePhone.replace(" ", ""); }
  set city (city) { this._userInfo.city = city; }
  set state (state) { this._userInfo.state = state; }
  set country (country) { this._userInfo.country = country; }
  set flag (flag) { this._userInfo.flag = flag; }
  set tz (timezone) { this._userInfo.tz = timezone; }
  set firm (firm) { this._userInfo.firm = firm; }
  set bio (bio) { this._userInfo.bio = bio; }
  set link (url) { this._userInfo.link = url; }
  set motto (motto) { this._userInfo.motto = motto; }
  set expertise (expertise) { this._userInfo.expertise = expertise; }
  set yearsOfExperience (years) { this._userInfo.yearsOfExperience = years; }
  set spoken (languages) { this._userInfo.spoken = languages; }
  set picture (picture) { this._userInfo.picture = picture; }
  set location (location) { this._userInfo.location = location; }
  set terms (acceptance) { this._userInfo.terms = acceptance; }
  set nameKey (nameKey) { this._userInfo.nameKey = nameKey; }
  set spentPoints(points) { this._userInfo.spentPoints = points; }
  set creationDate(date) {
    this._userInfo.creationDate = date;
  }

  // Public methods

  /**
   * @returns {ValidationResult}
   */
  validateCredentials () {
    const tmp = [];
    
    this._credentialsValidations.forEach(validator => tmp.push(validator));
    this._creationValidation.forEach(validator => tmp.push(validator));

    const valid = tmp
      .map(validator => validator(this._userInfo, this._counter))
      .filter(value => typeof value != "boolean");

    return { errors: valid, valid: valid.length == 0 };
  }

  /**
   * @returns {ValidationResult}
   */
  validate () {
    const valid = this._validations
      .map(validator => validator(this._userInfo, this._counter))
      .filter(value => typeof value != "boolean");

    return { errors: valid, valid: valid.length == 0 };
  }

  // Private methods
  
  /**
   * Initialize data model
   * 
   * @private
   */
  _init (defaultData = undefined) {
    this._id = defaultData != undefined ? defaultData._id : undefined;
    this._isValid = defaultData != undefined ? defaultData.isValid : undefined;
    this._userInfo = {
      name: defaultData != undefined ? defaultData.name : undefined,
      surname: defaultData != undefined ? defaultData.surname : undefined,
      username: defaultData != undefined ? defaultData.username : undefined,
      password: defaultData != undefined ? defaultData.password : undefined,
      firm: defaultData != undefined ? defaultData.firm : undefined,
      city: defaultData != undefined ? defaultData.city : undefined,
      state: defaultData != undefined ? defaultData.state : undefined,
      country: defaultData != undefined ? defaultData.country : undefined,
      flag: defaultData != undefined ? defaultData.flag : undefined,
      email: defaultData != undefined ? defaultData.email : undefined,
      phone: defaultData != undefined ? defaultData.phone : undefined,
      privatePhone: defaultData != undefined ? defaultData.privatePhone : undefined,
      timezone: defaultData != undefined ? defaultData.timezone : undefined,
      tz: defaultData != undefined && defaultData.timezone != undefined
          ? timezones.find(t => t.value == defaultData.timezone).value
          : undefined,
      expertise: defaultData != undefined ? defaultData.expertise : undefined,
      yearsOfExperience: defaultData != undefined && defaultData.yearsOfExperience != undefined
          ? defaultData.yearsOfExperience
          : 0,
      spokenLanguages: defaultData != undefined ? defaultData.spokenLanguages : undefined,
      spoken: defaultData != undefined && defaultData.spokenLanguages != undefined
          ? defaultData.spokenLanguages.map(l => langs.languages.find(lang => lang.value == l).value)
          : undefined,
      bio: defaultData != undefined ? defaultData.bio : undefined,
      link: defaultData != undefined ? defaultData.link : undefined,
      motto: defaultData != undefined ? defaultData.motto : undefined,
      picture: defaultData != undefined ? defaultData.picture : undefined,
      location: defaultData != undefined ? defaultData.location : undefined,
      terms: defaultData != undefined ? defaultData.terms : undefined,
      nameKey: defaultData != undefined ? defaultData.nameKey : undefined,
      spentPoints: defaultData != undefined ? defaultData.spentPoints : undefined,
      creationDate: defaultData != undefined ? defaultData.creationDate : undefined,
      roles: defaultData != undefined ? defaultData.roles : undefined
    };

    this._credentialsValidations.forEach(validator => this._validations.push(validator));
  }
}
